import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';

import { ApiService } from 'src/app/api/api.service';
import * as dayjs from 'dayjs';
import * as dayjsLocal from 'dayjs/plugin/localizedFormat';
import { AlertController, ModalController, PopoverController } from '@ionic/angular';
import { FilePage } from 'src/app/pop-overs/files/file/file.page';
import { AutomaticStepTypes, ClientStepTypes, StepTypes } from '../StepTypes';
import { GlobalService } from 'src/app/api/global.service';
import { StepTypes as stepTypeEnum } from '../StepTypes';

import { CreatePage as SurveyCreatePage } from '../../../surveys/create/create.page';
import { SelectPage as SurveySelectPage } from '../../../surveys/select/select.page';
import { ExplorerComponent } from 'src/app/components/files/explorer/explorer.component';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { SelectPage as TeamMemberSelectPage } from 'src/app/team-members/select/select.page';
import { EmailPreviewComponent } from '../../email-preview/email-preview.component';


@Component({
  selector: 'step-create-component',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.scss'],
})
export class CreateComponent implements OnInit {

    stepTypeEnum = stepTypeEnum;
    automaticStepTypes = AutomaticStepTypes;
    
  @Input() step: any;

  @Input() edit_mode = false;
  // Reviewing a "review before sending" action - only the email section is shown (no action details column).
  @Input() email_only = false;
  @Input() task: any = null;
  @Input() project: any = null;

  @Input() selected_template: any = null;
  email_templates = [];

  // Emitted when the user chose to mark the action complete from the checklist prompt, so whatever is
  // showing this action (the details page) can reload it.
  @Output() stepCompleted = new EventEmitter<void>();

  team_members: any[] = [];
  client_roles: any[] = [];
  resolved_recipient: { display_name: string, email: string } | null = null;
  appointment_team_members_valid: boolean = true;
  calendar_setup_warnings: string[] = [];
  private calendarSetupCache: Record<string, boolean> = {};

  @ViewChild('bodyEditor') bodyEditor: any;
  private lastCursorIndex: number | null = null;


  constructor(private modalController: ModalController,private apiService: ApiService,public globalService: GlobalService,private alertController: AlertController,private popoverController: PopoverController) { }

  ngOnInit() {
    if(!this.step.data) {
      if(this.step.steps_data) {
        this.step.data = JSON.parse(this.step.steps_data[0].step_data);
      }
    }
    if (this.step.data?.surveys?.length) {
      this.step.data.surveys = this.step.data.surveys
        .map((s: any) => Array.isArray(s) ? (s[0] ?? null) : s)
        .filter(Boolean);
    }
    if (this.step.data && this.step.data.use_cert === undefined) {
      this.step.data.use_cert = true;
    }
    this.EnsureChecklistData();
    this.GetEmailTemplates();
    this.GetTeamMembers();
    this.GetClientRoles();
    this.RefreshResolvedRecipient();
  }

  


  async OpenEmailPreview() {
    const modal = await this.modalController.create({
      component: EmailPreviewComponent,
      cssClass: 'large-modal',
      backdropDismiss: true,
      componentProps: {
        type: this.step.type,
        subject: this.step.data?.subject || '',
        description: this.step.data?.description || '',
        body: this.step.data?.body || '',
        contact: this.project?.assigned_clients?.[0] || null,
        company: this.project?.assigned_companies?.[0] || null,
        project: this.project || null,
        task: this.task || null,
        action: this.step || null,
      }
    });
    return await modal.present();
  }

  //Survey Stuff
  async OpenSurveyCreate() {
    const modal = await this.modalController.create({
      component: SurveyCreatePage,
      componentProps: {},
      backdropDismiss: true,
      cssClass: 'large-modal'
    });
    modal.present();

    const { data } = await modal.onWillDismiss();
    if (data) {
      if(!this.step.data.surveys) this.step.data.surveys = [];
      this.step.data.surveys.push(data);
    }
  }

  async OpenSurveySelect() {
    const modal = await this.modalController.create({
      component: SurveySelectPage,
      componentProps: {},
      backdropDismiss: true,
      cssClass: 'large-modal'
    });
    modal.present();

    const { data } = await modal.onWillDismiss();
    if (data) {
      if(!this.step.data.surveys) this.step.data.surveys = [];
      this.step.data.surveys.push(...data);
    }
  }

  // ---- Checklist action ----
  // The items live in step.data.items as { guid, text, checked, checked_date?, checked_by? }. While the action is
  // being planned (edit_mode) they're just edited and saved with the step; once it exists and isn't being edited
  // they're checked off / added / removed in place and saved straight away, so nothing is lost if the modal is closed.
  new_checklist_item = '';
  new_checklist_description = '';

  EnsureChecklistData() {
    if (Number(this.step?.type) !== stepTypeEnum.checklist && Number(this.step?.type) !== stepTypeEnum.checklist_request) return;
    if (!this.step.data) this.step.data = {};
    if (!Array.isArray(this.step.data.items)) this.step.data.items = [];
  }

  get checklistIsLive(): boolean {
    return !this.edit_mode && !!this.step?.guid;
  }

  get checklistLocked(): boolean {
    return this.step?.status == 1;
  }

  ChecklistCheckedCount(): number {
    return (this.step?.data?.items || []).filter((i: any) => i.checked).length;
  }

  ChecklistPercent(): number {
    const total = (this.step?.data?.items || []).length;
    return total === 0 ? 0 : Math.round((this.ChecklistCheckedCount() / total) * 100);
  }

  private NewChecklistItemGuid(): string {
    return (typeof crypto !== 'undefined' && (crypto as any).randomUUID)
      ? (crypto as any).randomUUID()
      : Date.now().toString(36) + Math.random().toString(36).slice(2);
  }

  // Returns false if the save failed. Nothing to save while the action is still being planned - the items go
  // with the step when it's created or updated.
  private async SaveChecklist(): Promise<boolean> {
    if (!this.checklistIsLive) return true;
    const data: any = await this.apiService.put(`steps/${this.step.guid}`, { step_data: JSON.stringify(this.step.data) });
    if (!data?.success) {
      this.globalService.presentToastMessage(data?.message || 'Could not save the checklist', false);
      return false;
    }
    return true;
  }

  async AddChecklistItem() {
    const text = (this.new_checklist_item || '').trim();
    if (!text) return;
    this.EnsureChecklistData();
    const description = (this.new_checklist_description || '').trim();
    const item: any = { guid: this.NewChecklistItemGuid(), text, checked: false };
    if (description) item.description = description;
    this.step.data.items.push(item);
    this.new_checklist_item = '';
    this.new_checklist_description = '';
    if (!(await this.SaveChecklist())) {
      const index = this.step.data.items.indexOf(item);
      if (index !== -1) this.step.data.items.splice(index, 1);
      this.new_checklist_item = text;
      this.new_checklist_description = description;
    }
  }

  async RemoveChecklistItem(item: any) {
    const index = this.step.data.items.indexOf(item);
    if (index === -1) return;
    this.step.data.items.splice(index, 1);
    if (!(await this.SaveChecklist())) this.step.data.items.splice(index, 0, item);
  }

  // [(ngModel)] has already flipped item.checked by the time this runs
  async ToggleChecklistItem(item: any) {
    if (item.checked) {
      item.checked_date = new Date().toISOString();
      item.checked_by_guid = this.globalService.GetCurrentUser()?.guid || '';
      item.checked_by = this.globalService.GetCurrentUser()?.display_name || '';
    } else {
      delete item.checked_date;
      delete item.checked_by_guid;
      delete item.checked_by;
    }

    if (!(await this.SaveChecklist())) {
      item.checked = !item.checked;
      return;
    }

    if (item.checked && this.checklistIsLive && !this.checklistLocked
      && this.step.data.items.length > 0 && this.ChecklistCheckedCount() === this.step.data.items.length) {
      await this.OfferCompleteChecklist();
    }
  }

  private async OfferCompleteChecklist() {
    const alert = await this.alertController.create({
      header: 'Checklist Complete',
      message: 'Everything on this checklist is checked off. Would you like to mark this action as complete?',
      buttons: [
        { text: 'Not Yet', role: 'cancel' },
        {
          text: 'Mark As Complete',
          handler: async () => {
            await this.globalService.presentLoading(`Completing ${this.step.title}`, 30000);
            const data: any = await this.apiService.put(`steps/${this.step.guid}/complete/true`, null);
            await this.globalService.dismissLoading();
            if (data?.success === false) {
              this.globalService.presentToastMessage(data.message || 'Could not complete the action', false);
              return;
            }
            this.stepCompleted.emit();
          }
        }
      ]
    });
    await alert.present();
  }

  async TypeChange() {
    this.EnsureChecklistData();
    if(!this.automaticStepTypes.includes(Number(this.step.type))) {
      this.step.auto_start = false;
    }
    await this.CheckCalendarSetups();
  }

  async GetTeamMembers() {
    const data: any = await this.apiService.get('team_members');
    if (data.success) this.team_members = data.data || [];
  }

  async GetClientRoles() {
    const data: any = await this.apiService.GetRoles();
    if (data.success) this.client_roles = data.data || [];
    await this.RefreshRoleContactMap();
  }

  isClientStepType(): boolean {
    return ClientStepTypes.includes(Number(this.step?.type));
  }

  getAvailableOverrideRoles(): any[] {
    return this.client_roles || [];
  }

  private getProjectMinRoleLevel(): number | null {
    const companiesWithRoles = (this.project?.assigned_companies || [])
      .filter((c: any) => c.role_guid && c.role_level != null);
    if (companiesWithRoles.length) {
      return Math.min(...companiesWithRoles.map((c: any) => Number(c.role_level)));
    }
    if (this.project?.role_guid && this.client_roles?.length) {
      const projectRole = this.client_roles.find((r: any) => r.guid === this.project.role_guid);
      if (projectRole?.level != null) return Number(projectRole.level);
    }
    return null;
  }

  isOverrideRoleDisabled(role: any): boolean {
    const minLevel = this.getProjectMinRoleLevel();
    if (minLevel === null) return false;
    return role.level != null && Number(role.level) > minLevel;
  }

  hasDisabledOverrideRoles(): boolean {
    return (this.client_roles || []).some((r: any) => this.isOverrideRoleDisabled(r));
  }

  getRoleName(guid: string): string {
    if (!guid) return '';
    const role = this.client_roles.find(r => r.guid === guid);
    return role ? role.title : guid;
  }

  // Which contact (if any) is the default for each role on the assigned company - populated once
  // client_roles/step.company_guid are known (see GetClientRoles) so the Target Role dropdown can
  // show name + email per role without an async call per template render.
  role_contact_map: { [role_guid: string]: { display_name: string, email: string } | null } = {};

  async RefreshRoleContactMap() {
    this.role_contact_map = {};
    if (!this.step?.company_guid) return;
    const full_company = await this.globalService.GetCompany(this.step.company_guid);
    if (!full_company || !Array.isArray(full_company.clients)) return;
    for (const role of this.client_roles || []) {
      const match = full_company.clients.find((c: any) => c.role_guid === role.guid && c.is_default_for_role);
      if (!match) { this.role_contact_map[role.guid] = null; continue; }
      const client = await this.globalService.GetClient(match.guid);
      this.role_contact_map[role.guid] = client ? { display_name: client.display_name, email: client.email || '' } : null;
    }
  }

  // Options for the Target Role app-description-dropdown (same component missing-info/create uses
  // for its Missing Info Type field) - each option's description shows the name/email of whichever
  // contact is actually the default for that role, so the accountant can see exactly who a role
  // routes to before picking it, instead of just a role title.
  get overrideRoleOptions(): any[] {
    return this.getAvailableOverrideRoles().map((role: any) => {
      const contact = this.role_contact_map[role.guid];
      let description: string;
      if (contact) {
        description = contact.email ? `${contact.display_name} (${contact.email})` : `${contact.display_name} — no email on file`;
      } else {
        description = 'No contact assigned to this role';
      }
      return {
        value: role.guid,
        label: `${role.title} (Level ${role.level})`,
        description,
        disabled: this.isOverrideRoleDisabled(role)
      };
    });
  }

  // Resolves who this notification will actually be sent to right now - the default contact for
  // the step's role, or (when send_to_different_role is on) the default contact for the override
  // role instead - mirroring Steps.cs's own resolution (lookup_role -> role_guid or
  // override_role_guid -> Companies.GetDefaultClientGUIDForRole) so what's shown here matches
  // where the email actually goes. The step itself (not the project) carries company_guid/
  // client_guid/role_guid - a project can have multiple assigned companies/clients, and the step
  // is scoped to one specific one of them (see UpdateStep/OpenESignatureSetup in
  // steps/details/details.page.ts, which read the same step.company_guid/client_guid/role_guid).
  // Cached on resolved_recipient rather than computed inline in the template since resolving a
  // company's role-assigned contact needs an (cached) API call.
  async RefreshResolvedRecipient() {
    if (!this.isClientStepType()) { this.resolved_recipient = null; return; }

    if (this.step?.company_guid) {
      const full_company = await this.globalService.GetCompany(this.step.company_guid);
      if (!full_company) { this.resolved_recipient = null; return; }

      const role_guid = (this.step?.data?.send_to_different_role && this.step?.data?.override_role_guid)
        ? this.step.data.override_role_guid
        : (this.step?.role_guid || full_company.role_guid || '');

      // company.clients (from the cached bulk company list GlobalService.GetCompany reads from
      // first) only carries guid/role_guid/is_default_for_role - no display_name/email - so once
      // we know which contact guid to use, fetch that client's own record for the real details.
      let target_guid = '';
      if (role_guid && Array.isArray(full_company.clients)) {
        const roleMatch = full_company.clients.find((c: any) => c.role_guid === role_guid && c.is_default_for_role);
        if (roleMatch) target_guid = roleMatch.guid;
      }
      if (!target_guid && full_company.default_client_guid) {
        target_guid = full_company.default_client_guid;
      }

      const client = target_guid ? await this.globalService.GetClient(target_guid) : null;
      console.log("Resolved recipient client:", client);
      this.resolved_recipient = client ? { display_name: client.display_name, email: client.email || '' } : null;
      return;
    }

    if (this.step?.client_guid) {
      const client = await this.globalService.GetClient(this.step.client_guid);
      this.resolved_recipient = client ? { display_name: client.display_name, email: client.email || '' } : null;
      return;
    }

    this.resolved_recipient = null;
  }

  RemoveAppointmentTeamMember(tm: any) {
    this.step.data.allowed_team_members =
      (this.step.data.allowed_team_members || []).filter((m: any) => m.guid !== tm.guid);
    this.CheckCalendarSetups();
  }

  async presentSelectAppointmentTeamMembers() {
    if (!this.step.data.allowed_team_members) this.step.data.allowed_team_members = [];
    const modal = await this.modalController.create({
      component: TeamMemberSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        selecting_for_company: false,
        selecting_for_projects: false,
        selected_team_members: this.step.data.allowed_team_members,
        remove_team_members: this.step.data.allowed_team_members,
        in_team_members: [],
        target_header: 'Currently Selected'
      }
    });
    modal.onDidDismiss().then(() => {
      this.CheckCalendarSetups();
    });
    return await modal.present();
  }

  OnAnyAppointmentTeamMemberChange() {
    if (!this.step.data.any_team_member && !this.step.data.allowed_team_members) {
      this.step.data.allowed_team_members = [];
    }
    this.CheckCalendarSetups();
  }

  async CheckCalendarSetups() {
    if (Number(this.step.type) !== stepTypeEnum.appointment_request) {
      this.calendar_setup_warnings = [];
      return;
    }
    const tms_to_check = this.step.data?.any_team_member
      ? this.team_members
      : (this.step.data?.allowed_team_members || []);
    const warnings: string[] = [];
    for (const tm of tms_to_check) {
      if (!(tm.guid in this.calendarSetupCache)) {
        const data: any = await this.apiService.get(`calendar/setup/${tm.guid}`);
        this.calendarSetupCache[tm.guid] = data?.success && Array.isArray(data.data) && data.data.length > 0;
      }
      if (!this.calendarSetupCache[tm.guid]) warnings.push(tm.display_name);
    }
    this.calendar_setup_warnings = warnings;
  }

  canCompleteConditionalStep(): boolean {
    console.log("STEP STATUS", this.step.status);
    if (!this.step || this.step.status == 1) return false;
    if(this.step.status == 2) return true;
    if (!this.task || !this.task.steps) return true;
    const stepIndex = this.task.steps.findIndex((s: any) => s.guid === this.step.guid);
    if (stepIndex <= 0) return true;
    return this.task.steps[stepIndex - 1]?.status == 1;
  }

  get selectedConditionalOptionData(): any {
    if (!this.step?.data?.options) return null;
    const idx = this.step.data?.selected_option_index;
    if (idx == null || idx === undefined) return null;
    return this.step.data.options[idx] ?? null;
  }

  async SelectConditionalOption(option: any, index: number) {
    const alert = await this.alertController.create({
      header: 'Confirm Selection',
      message: `Are you sure you want to select "${option.label}"? This will complete this action.`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: async () => {
            await this.globalService.presentLoading('Completing action...', 30000);
            this.step.data.selected_option_index = index;
            this.step.data.selected_option_label = option.label;
            let put_data = {
              data: JSON.stringify(this.step.data)
            }
            await this.apiService.put(`steps/${this.step.guid}/complete/true`, put_data);
            this.step.status = 1;
            await this.globalService.dismissLoading();
          }
        }
      ]
    });
    await alert.present();
  }

  isAutomaticStep() {
    return this.automaticStepTypes.includes(Number(this.step.type));
  }

  // Same rules as the task builder (tasks/create): "Review before sending" and Automatically Start can't
  // both be on. Review only applies to client actions that send an email, so a leftover value on another
  // type doesn't lock Automatically Start.
  ReviewBlocksAutoStart(): boolean {
    return this.isClientStepType() && !!this.step.data?.review_before_sending;
  }

  // Tooltip on Automatically Start - why it's disabled ('' shows nothing).
  AutoStartTooltip(): string {
    if (!this.isAutomaticStep()) return 'Disabled because this action type cannot start automatically.';
    if (this.ReviewBlocksAutoStart()) return 'Disabled because Review before sending is on - an automatic start has no one to review the email before it is sent.';
    return '';
  }

  ReviewBeforeSendingChange() {
    if (this.step.data?.review_before_sending) this.step.auto_start = false;
  }

  // Automatically Start off means someone starts the action by hand, so the email defaults back to being
  // reviewed before it's sent (still just a default - they can untick it).
  AutoStartChange() {
    if (!this.step.data) return;
    this.step.data.review_before_sending = !this.step.auto_start;
  }

  CalculateDueDateStep() {

  }

  ShowDateLocal(due_date) {
    return due_date;
  }

  AddStep() {

  }

  async GetEmailTemplates() {
    let data: any = await this.apiService.get('email_templates');
    this.email_templates = data.data;
  }

  //Email template
  async EmailTemplateChange() {
    this.step.data.body = '';
    var selected_template = this.email_templates.find(template => template.guid == this.selected_template);
    if(selected_template) this.step.data.body = selected_template.body;
    
  }

  async AddFileRequest() {
    if (!this.step.data.specific_files) this.step.data.specific_files = [];
    // Make sure AlertController is imported and injected in the constructor
    // constructor(private modalController: ModalController, private alertController: AlertController, ...)
    const alert = await this.alertController.create({
      backdropDismiss: false,
      header: 'Add File Request',
      inputs: [
        {
          name: 'name',
          type: 'text',
          placeholder: 'File Name'
        },
        {
          name: 'description',
          type: 'text',
          placeholder: 'Description'
        }
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'Add',
          handler: (data) => {
            if (!data.name) {
              this.globalService.presentToastMessage('A name is required.', false);
              return false;
            }
            this.step.data.specific_files.push({ name: data.name, description: data.description || '' });
          }
        }
      ]
    });
    await alert.present();
  }

  async RemoveSpecificFile(name) {
    this.globalService.RemoveFromArray(this.step.data.specific_files,name);
  }

   async RemoveSurvey(survey) {
    this.globalService.RemoveFromArray(this.step.data.surveys,survey.guid);
  }


  //File DROPZONE
      async dropped(files: NgxFileDropEntry[]) {
        if(this.step.data.file_list == null) this.step.data.file_list = [];
        for (const droppedFile of files) {
          // Is it a file?
          if (droppedFile.fileEntry.isFile) {
    
            const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
    
            await fileEntry.file(async (file: File) => {
              let file_guid = await this.apiService.UploadFile(file,'0',null);
              if(file_guid != null) {
                let push_data = {
                  guid: file_guid,
                  file_name: file.name
                }
                this.step.data.file_list.push(push_data);
              }
            });
          } else {
            this.globalService.presentToastMessage('Only files are allowed', false);
          }
        }
      }
    
      async RemoveFile(file) {
        this.globalService.RemoveFromArray(this.step.data.file_list, file.guid);
      }
    
      async OpenFileSelect() {
        const modal = await this.modalController.create({
          component: ExplorerComponent,
          componentProps: {
            file_select_mode: true,
            root_folder_guid: '0'
          },
          backdropDismiss: true,
          cssClass: 'large-modal'
        });
        modal.present();
    
        const { data, role } = await modal.onWillDismiss();
        if (data) {
          for (let i = 0; i < data.length; i++) {
            data[i].was_selected = true;
            this.step.data.file_list.push(data[i]);
          }
        }
      }

      async DownloadFile(file) {
        this.apiService.DownloadFile(file);
      }

      async presentFilePopover(e, file) {
        e.stopPropagation();
        e.preventDefault();
        const popover = await this.popoverController.create({
          component: FilePage,
          componentProps: { file: file, selected_files: [], minimal: true, show_go_to_folder: true },
          event: e,
          animated: true,
          backdropDismiss: true
        });
        popover.onDidDismiss().then((data) => {
          if (data.data == 4) this.apiService.DownloadFile(file);
          else if (data.data == 8) this.ToggleImportant(file);
          else if (data.data == 9) this.globalService.GoToFileFolder(file.parent_folder_guid);
        });
        return await popover.present();
      }

      async ToggleImportant(file: any) {
        const new_value = !file.is_important;
        const data: any = await this.apiService.put(`files/${file.guid}/important`, { important: new_value });
        if (data.success) {
          file.is_important = new_value;
          this.globalService.presentToastMessage(new_value ? 'Marked as important' : 'Removed from important', true);
        } else {
          this.globalService.presentToastMessage(data.message, false);
        }
      }


      //Logic for the better editor UI.
      OnEditorSelectionChanged(event: any) {
    if (event.range !== null) {
      this.lastCursorIndex = event.range.index;
    }
  }

  OnEditorContentChanged(event: any) {
    const range = event.editor?.getSelection();
    if (range !== null) {
      this.lastCursorIndex = range?.index ?? this.lastCursorIndex;
    }
  }

  AppendToStepSubject(field: string) {
    const current = this.step.data.subject || '';
    const prefix = current.length > 0 && current[current.length - 1] !== ' ' ? ' ' : '';
    this.step.data.subject = current + prefix + field + ' ';
  }

  AppendToStepDescription(field: string) {
    const current = this.step.data.description || '';
    const prefix = current.length > 0 && current[current.length - 1] !== ' ' ? ' ' : '';
    this.step.data.description = current + prefix + field + ' ';
  }

  AppendToBody(field: string) {
    const quill = this.bodyEditor?.quillEditor;
    if (quill) {
      const index = this.lastCursorIndex ?? quill.getLength();
      const charBefore = index > 0 ? quill.getText(index - 1, 1) : ' ';
      const charAfter = quill.getText(index, 1);
      const prefix = charBefore !== ' ' ? ' ' : '';
      const suffix = charAfter !== ' ' && charAfter !== '.' && charAfter !== '\n' && charAfter !== '' ? ' ' : '';
      const textToInsert = prefix + field + suffix;
      quill.insertText(index, textToInsert, 'user');
      const newIndex = index + textToInsert.length;
      quill.setSelection(newIndex, 0);
      this.lastCursorIndex = newIndex;
      // Sync ngModel
      this.step.data.body = quill.root.innerHTML;
    } else {
      this.step.data.body = (this.step.data.body || '') + field;
    }
  }


      



}
